'use client';

import CustomLink from '@pickleballinc/ui/components/ui/custom-link';
import { useSearchParams } from 'next/navigation';

import ImageWithFallback from '@/components/ImageWithFallback/ImageWithFallback';
import { RankChange } from '@/modules/rankings/components/RankChange';
import { EventsBreakdown } from '@/modules/rankings/PlayerCard/EventsBreakdown';
import { formatPoints } from '@/modules/rankings/rankingsUtils';

export interface MobilePlayerCardProps {
  rank: string;
  firstName: string;
  lastName: string;
  country: string;
  flagUrl: string;
  played: number;
  doublesPlayed: number;
  mixedPlayed: number;
  singlesPlayed: number;
  gender: string;
  points: number;
  livePoints: number;
  change?: number;
  nextPoints?: number;
  maxPoints?: number;
  currentTournamentText?: string;
  slug: string;
  uuid: string;
}

export const MobilePlayerCard = ({
  rank,
  firstName,
  lastName,
  country,
  flagUrl,
  played,
  doublesPlayed,
  mixedPlayed,
  singlesPlayed,
  gender,
  points,
  livePoints,
  change,
  nextPoints,
  maxPoints,
  currentTournamentText,
  slug,
  uuid,
}: MobilePlayerCardProps) => {
  const getShortName = (name: string) => name.charAt(0) + '.';
  const searchParams = useSearchParams();
  const live = searchParams?.get('live') ?? '';
  const isCurrentSeed = searchParams?.get('ranking_type') === 'currentSeed';
  const playerRedirectURL = `/rankings/players/${slug || uuid}?type=player-profile`;

  return !live ? (
    <div className='container m-0 flex w-full items-center justify-between border-b border-gray-200 py-2 pl-3'>
      {/* Rank */}
      <div className='flex w-[15%] items-center justify-start pl-3 text-sm font-semibold'>
        <span className='w-[55%]'>{rank}</span>
        <span className='w-[45%]'>
          <RankChange change={change ? change : 0} />
        </span>
      </div>
      {/* Picture and name */}
      <CustomLink
        href={playerRedirectURL}
        className='flex w-[35%] min-w-0 items-center justify-start space-x-1 overflow-hidden'
      >
        <ImageWithFallback
          src={flagUrl ? flagUrl : undefined}
          alt={country}
          className='size-[18px] shrink-0 rounded-full'
          width={18}
          height={18}
        />
        <p className='shrink-0 text-sm text-gray-900'>{getShortName(firstName)}</p>
        <p className='truncate text-sm text-gray-900'>{lastName}</p>
      </CustomLink>
      {/* Played */}
      <div className='flex w-[30%] flex-col items-center justify-center text-sm text-gray-900'>
        <span>{played}</span>
        {!isCurrentSeed ? (
          <EventsBreakdown
            doubles={doublesPlayed}
            mixed={mixedPlayed}
            singles={singlesPlayed}
            gender={gender}
            className='mt-0.5 flex justify-center gap-x-1.5 text-[10px] leading-tight text-gray-500'
          />
        ) : null}
      </div>
      {/* Score */}
      <div className='flex w-[20%] items-center justify-center text-sm font-semibold text-brand-700'>
        {formatPoints(points)}
      </div>
    </div>
  ) : (
    <div
      className={`container m-0 flex w-full items-center justify-between border-b border-gray-200 py-2 pl-3 ${currentTournamentText ? 'bg-brand-25' : 'bg-white'}`}
    >
      {/* Rank */}
      <div className='flex w-1/5 items-center justify-between text-sm font-semibold'>
        <span className='w-1/2 pl-3'>{rank}</span>
        <span className='w-1/2'>
          <RankChange change={change ? change : 0} />
        </span>
      </div>
      {/* Picture and name */}
      <div className='flex w-2/5 flex-col'>
        <CustomLink
          href={playerRedirectURL}
          className='flex items-center justify-start space-x-1 overflow-hidden rounded-full'
        >
          <ImageWithFallback
            src={flagUrl ? flagUrl : undefined}
            alt={country}
            className='size-[18px] rounded-full'
            width={18}
            height={18}
          />
          <p className='text-sm text-gray-900'>{getShortName(firstName)}</p>
          <p className='text-sm text-gray-900'>{lastName}</p>
        </CustomLink>
        {currentTournamentText ? (
          <span className='w-[56%] text-xs'>{currentTournamentText}</span>
        ) : null}
      </div>
      {/* Live Points */}
      <div className='flex w-1/5 items-center justify-center text-sm font-semibold text-brand-700'>
        {formatPoints(livePoints)}
      </div>
      {/* Next and Max Points */}
      <div className='flex w-1/5 flex-col items-center justify-center'>
        <div className='text-sm font-semibold text-gray-900'>
          {nextPoints != null ? formatPoints(nextPoints) : '--'}
        </div>
        <div className='text-sm text-gray-500'>
          {maxPoints != null ? formatPoints(maxPoints) : '--'}
        </div>
      </div>
    </div>
  );
};
